import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from "react";

import { Configuration } from "@azure/msal-browser";

import { usePermissions } from "./PermissionsContext";
import { useGetSecretsQuery } from "@/api/useGetSecretsQuery";
import { Teams } from "@/api/useGetTeams";
import { TeamsFile } from "@/util/interfaces/TeamsFile";
import { Permission } from "@/utils/policies";

interface TeamsContextType {
  teamsAndChannels: Teams;
  setTeamsAndChannels: Dispatch<SetStateAction<Teams>>;
  msalConfig: TeamsConfig;
  teamsFiles: TeamsFile[];
  setTeamsFiles: Dispatch<SetStateAction<TeamsFile[]>>;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
}
interface TeamsConfig extends Configuration {
  loginRequest?: { scopes: string[] };
  backend_uri?: { scopes: string[] };
}
const TeamsAndChannelsContext = createContext<TeamsContextType | undefined>(undefined);

export const TeamsAndChannelsProvider = ({ children }: { children: React.ReactNode }) => {
  const { hasPermission } = usePermissions();
  const [teamsAndChannels, setTeamsAndChannels] = useState<Teams>([]);
  const [teamsFiles, setTeamsFiles] = useState<TeamsFile[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const {
    data: secrets,
    isLoading: secretsLoading,
    error: secretsError,
  } = useGetSecretsQuery(hasPermission(Permission.IMPORT_TEAMS));
  const [msalConfig, setMsalConfig] = useState<TeamsConfig>({ auth: { clientId: "" } });
  useEffect(() => {
    if (!secretsLoading && !secretsError && secrets) {
      setMsalConfig({
        auth: {
          clientId: secrets.AZURE_CLIENT_ID,
          authority: `https://login.microsoftonline.com/${secrets.AZURE_TENANT_ID}`,
          redirectUri: process.env.REACT_APP_CC_USER_MANAGEMENT_APP_URL ?? "http://localhost:3001",
        },
        cache: {
          cacheLocation: "localStorage",
          storeAuthStateInCookie: false,
        },
        loginRequest: {
          scopes: ["User.Read"],
        },
        backend_uri: {
          scopes: [`api://${secrets.AZURE_CLIENT_ID}/user_impersonation`],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secrets?.AZURE_CLIENT_ID, secrets?.AZURE_TENANT_ID, secretsLoading, secretsError]);

  const value = useMemo(
    () => ({
      teamsAndChannels,
      setTeamsAndChannels,
      msalConfig,
      teamsFiles,
      setTeamsFiles,
      selectedRows,
      setSelectedRows,
    }),
    [teamsAndChannels, msalConfig, teamsFiles, selectedRows]
  );

  return (
    <TeamsAndChannelsContext.Provider value={value}>{children}</TeamsAndChannelsContext.Provider>
  );
};

export const useTeamsAndChannels = (): TeamsContextType => {
  const context = useContext(TeamsAndChannelsContext);
  if (!context) throw new Error("useTeams must be used within a TeamsProvider");
  return context;
};
